'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Phone, MessageCircle, Calendar, Plane, Star, Shield, Award } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

interface FinalCTASectionProps {
    treatmentName: string;
    treatmentKeyword?: string; // For WhatsApp message
    scrollToSection?: string; // ID of section to scroll to for consultation
    customHeading?: string | ReactNode;
    customSubheading?: string;
    customBenefits?: string[];
    namespace?: string; // Translation namespace to use
}

export default function FinalCTASection({
    treatmentName,
    treatmentKeyword = treatmentName.toLowerCase(),
    scrollToSection = 'hero',
    customHeading,
    customSubheading,
    customBenefits,
    namespace = 'hollywood-smile'
}: FinalCTASectionProps) {
    const { t } = useTranslation(namespace as any);
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const sectionRef = useRef<HTMLElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);

    const trustIndicators = [
        {
            icon: <Star className="w-6 h-6 text-gold" />,
            text: t('cta.trust_indicators.rating.text'),
            subtext: t('cta.trust_indicators.rating.subtext')
        },
        {
            icon: <Shield className="w-6 h-6 text-gold" />,
            text: t('cta.trust_indicators.warranty.text'),
            subtext: t('cta.trust_indicators.warranty.subtext')
        },
        {
            icon: <Award className="w-6 h-6 text-gold" />,
            text: t('cta.trust_indicators.experience.text'),
            subtext: t('cta.trust_indicators.experience.subtext')
        },
        {
            icon: <Plane className="w-6 h-6 text-gold" />,
            text: t('cta.trust_indicators.travel.text'),
            subtext: t('cta.trust_indicators.travel.subtext')
        }
    ];

    const ctaButtons = [
        {
            icon: <Calendar className="w-5 h-5" />,
            text: t('cta.buttons.consultation.text'),
            description: t('cta.buttons.consultation.description'),
            primary: true,
            action: 'consultation'
        },
        {
            icon: isRTL ? <Phone className="w-5 h-5 scale-x-[-1]" /> : <Phone className="w-5 h-5" />,
            text: t('cta.buttons.call.text'),
            description: t('cta.buttons.call.description'),
            primary: false,
            action: 'call'
        },
        {
            icon: <MessageCircle className="w-5 h-5" />,
            text: t('cta.buttons.whatsapp.text'),
            description: t('cta.buttons.whatsapp.description'),
            primary: false,
            action: 'whatsapp'
        }
    ];

    const defaultBenefits = [
        t('cta.default_benefits.0'),
        t('cta.default_benefits.1'),
        t('cta.default_benefits.2'),
        t('cta.default_benefits.3')
    ];

    useEffect(() => {
        if (!sectionRef.current || !ctaRef.current) return;

        const ctx = gsap.context(() => {
            // Main CTA animation
            if (ctaRef.current) {
                gsap.fromTo(ctaRef.current,
                    { opacity: 0, y: 60, scale: 0.95 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 1.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: sectionRef.current,
                            start: "top 70%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Stagger trust indicators
            if (ctaRef.current) {
                const indicators = ctaRef.current.querySelectorAll('.trust-indicator');
                if (indicators.length > 0) {
                    gsap.fromTo(indicators,
                        { opacity: 0, y: 30 },
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.6,
                            stagger: 0.1,
                            delay: 0.3,
                            ease: "power3.out",
                            scrollTrigger: {
                                trigger: sectionRef.current,
                                start: "top 70%",
                                toggleActions: "play none none reverse"
                            }
                        }
                    );
                }
            }

            // CTA buttons animation
            if (ctaRef.current) {
                const buttons = ctaRef.current.querySelectorAll('.cta-button');
                if (buttons.length > 0) {
                    gsap.fromTo(buttons,
                        { opacity: 0, y: 20, scale: 0.9 },
                        {
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            duration: 0.5,
                            stagger: 0.1,
                            delay: 0.6,
                            ease: "back.out(1.7)",
                            scrollTrigger: {
                                trigger: sectionRef.current,
                                start: "top 70%",
                                toggleActions: "play none none reverse"
                            }
                        }
                    );
                }
            }
        }, sectionRef);

        return () => {
            ctx.revert();
        };
    }, []);

    const handleCTAClick = (action: string) => {
        switch (action) {
            case 'consultation':
                if (scrollToSection === 'booking') {
                    window.open('/book-appointment', '_blank');
                } else {
                    const targetSection = document.getElementById(scrollToSection);
                    targetSection?.scrollIntoView({ behavior: 'smooth' });
                }
                break;
            case 'call':
                window.open('tel:+905070808888', '_self');
                break;
            case 'whatsapp':
                window.open(`https://wa.me/905070808888?text=Hello, I'm interested in ${treatmentKeyword} at DentaWista.`, '_blank');
                break;
        }
    };

    return (
        <section
            id="final-cta"
            ref={sectionRef}
            className="py-20 bg-gradient-to-br from-navy via-navy-950 to-navy relative overflow-hidden"
        >
            {/* Background decorations */}
            <div className="absolute inset-0">
                <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold opacity-10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-gold opacity-10 rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gold/5 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative">
                <div ref={ctaRef} className="max-w-4xl mx-auto text-center">
                    {/* Main heading */}
                    <div className="mb-12">
                        <h2 className={cn(
                            "text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-serif leading-tight",
                            isRTL && "font-arabic"
                        )}>
                            {customHeading || (
                                <>{t('cta.default_heading_part1')}{' '}
                                    <span className="text-gold">{t('cta.default_heading_part2')}</span></>
                            )}
                        </h2>
                        <p className={cn(
                            "text-xl md:text-2xl text-slate-300 leading-relaxed max-w-3xl mx-auto",
                            isRTL && "font-arabic"
                        )}>
                            {customSubheading || t('cta.default_subheading').replace('{{treatment}}', treatmentName.toLowerCase())}
                        </p>
                    </div>

                    {/* Trust indicators */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                        {trustIndicators.map((indicator, index) => (
                            <div key={index} className="trust-indicator text-center">
                                <div className="flex justify-center mb-3">
                                    <div className="p-3 rounded-xl bg-gold/20 backdrop-blur-xs">
                                        {indicator.icon}
                                    </div>
                                </div>
                                <div className={cn(
                                    "text-white font-semibold text-sm",
                                    isRTL ? "font-arabic" : ""
                                )}>
                                    {localizeText(indicator.text)}
                                </div>
                                <div className={cn(
                                    "text-slate-300 text-xs",
                                    isRTL ? "font-arabic" : ""
                                )}>
                                    {localizeText(indicator.subtext)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* CTA Buttons */}
                    <div className="grid md:grid-cols-3 gap-6 mb-12">
                        {ctaButtons.map((button, index) => {
                            let buttonStyles = '';

                            if (button.primary) {
                                // Primary consultation button - bright first, darker on hover
                                buttonStyles = 'bg-gold hover:bg-gold-600 text-navy border-2 border-gold hover:border-gold-600';
                            } else if (button.action === 'whatsapp') {
                                // WhatsApp button - green
                                buttonStyles = 'bg-[#25D366] hover:bg-[#22C55E] text-white border-2 border-[#25D366] hover:border-[#22C55E]';
                            } else if (button.action === 'call') {
                                // Phone call button - blue
                                buttonStyles = 'bg-[#3B82F6] hover:bg-[#2563EB] text-white border-2 border-[#3B82F6] hover:border-[#2563EB]';
                            } else {
                                // Default styling for any other buttons
                                buttonStyles = 'bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 hover:border-white/50 backdrop-blur-xs';
                            }

                            return (
                                <div
                                    key={index}
                                    className="cta-button"
                                >
                                    <Button
                                        size="lg"
                                        onClick={() => handleCTAClick(button.action)}
                                        className={`w-full h-full min-h-[100px] py-6 px-8 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl ${buttonStyles}`}
                                    >
                                        <div className="flex flex-col items-center space-y-2">
                                            <div className={cn(
                                                "flex items-center md:whitespace-nowrap",
                                                isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                            )}>
                                                {button.icon}
                                                <span className={cn(
                                                    "text-base md:text-lg",
                                                    isRTL && "font-arabic"
                                                )}>
                                                    {button.text}
                                                </span>
                                            </div>
                                            <span className={cn(
                                                `text-xs md:text-sm ${button.primary ? 'text-navy/80' : 'text-white/80'}`,
                                                isRTL && "font-arabic"
                                            )}>
                                                {button.description}
                                            </span>
                                        </div>
                                    </Button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Contact info */}
                    <div className={cn(
                        "bg-white/10 backdrop-blur-xs rounded-2xl p-8 border border-white/20",
                        isRTL ? "text-right" : "text-left"
                    )}>
                        <div className={cn(
                            "grid md:grid-cols-2 gap-8",
                            isRTL ? "text-right" : "text-left"
                        )}>
                            <div>
                                <h3 className={cn(
                                    "text-xl font-semibold text-white mb-4",
                                    isRTL && "font-arabic"
                                )}>
                                    {t('cta.contact_info.ready_to_book')}
                                </h3>
                                <ul className={cn(
                                    "space-y-2 text-slate-300",
                                    isRTL && "font-arabic"
                                )}>
                                    {(Array.isArray(customBenefits) ? customBenefits : defaultBenefits).map((benefit, index) => (
                                        <li key={index} className={cn(
                                            "flex items-start gap-3",
                                            isRTL ? "justify-end" : "justify-start"
                                        )}>
                                            <span className="text-gold flex-shrink-0">✓</span>
                                            <span className={cn(
                                                "flex-1",
                                                isRTL ? "text-right" : "text-left"
                                            )}>{localizeText(benefit)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="text-center">
                                <h3 className={cn(
                                    "text-xl font-semibold text-white mb-4",
                                    isRTL && "font-arabic"
                                )}>
                                    {t('cta.contact_info.questions_247')}
                                </h3>
                                <div className="space-y-3 flex flex-col items-center">
                                    <div className="flex items-center gap-3">
                                        <Phone className={cn(
                                            "w-5 h-5 text-gold",
                                            isRTL && "scale-x-[-1]"
                                        )} />
                                        <span className={cn(
                                            "text-slate-300 phone-number",
                                            isRTL && "font-arabic"
                                        )}>{localizeText("+90 507 080 8888")}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <MessageCircle className="w-5 h-5 text-gold" />
                                        <span className={cn(
                                            "text-slate-300",
                                            isRTL && "font-arabic"
                                        )}>{t('cta.contact_info.whatsapp_support')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Final note */}
                    <div className="mt-8 text-center">
                        <p className={cn(
                            "text-slate-300 text-sm",
                            isRTL && "font-arabic"
                        )}>
                            {t('cta.final_note')}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
} 
